// oxlint-disable-next-line no-restricted-imports
import { Group, Modal as MantineModal, Stack } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { PropsWithChildren } from "react";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { MODAL_Z_INDEX } from "#app/theme/v2/zIndex";
import { Button, type ButtonVariant } from "#components/atoms/Button/Button";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import { Flex } from "#shared/view/components/Flex";

type Props = PropsWithChildren<{
  opened: boolean;
  title: string;
  subtitle?: string;
  primaryText: string;
  closeText: string;
  secondaryText?: string;
  onSecondary?: () => void;
  onClose: () => void;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  primaryIcon?: React.ReactNode;
  primaryVariant?: Exclude<ButtonVariant, "link">;
  orientation?: "vertical" | "horizontal";
  variant?: "default" | "warning";
  icon?: React.ReactNode;
}>;

const MODAL_SIZE = {
  horizontal: 1032,
  vertical: 584,
  warning: 480,
};

const Modal = ({
  opened,
  title,
  subtitle,
  primaryText,
  closeText,
  secondaryText,
  onSecondary,
  onClose,
  onPrimary,
  primaryDisabled,
  primaryLoading,
  primaryIcon,
  primaryVariant = "primary",
  children,
  orientation = "vertical",
  variant = "default",
  icon,
}: Props) => {
  const isWarning = variant === "warning";
  const secondaryButton = (
    <Button variant="secondary" size="lg" fullWidth={isWarning} onClick={onSecondary ?? onClose}>
      {secondaryText ?? closeText}
    </Button>
  );
  const primaryButton = (
    <Button
      variant={primaryVariant}
      size="lg"
      fullWidth={isWarning}
      disabled={primaryDisabled}
      loading={primaryLoading}
      onClick={onPrimary}
      leftSection={primaryIcon}
    >
      {primaryText}
    </Button>
  );

  return (
    <MantineModal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size={isWarning ? MODAL_SIZE.warning : MODAL_SIZE[orientation]}
      padding={0}
      zIndex={MODAL_Z_INDEX}
      radius={radius.xxxl}
      overlayProps={{ color: colors.bg.elevation.overlayBlack34 }}
      styles={{
        content: { maxHeight: "90vh", display: "flex", flexDirection: "column" },
        body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: 0 },
      }}
    >
      <Group
        justify={isWarning ? "flex-end" : "space-between"}
        align="flex-start"
        style={{
          flexShrink: 0,
          padding: isWarning
            ? `${spacing[4]} ${spacing[4]} ${spacing[2]} ${spacing[8]}`
            : `${spacing[4]} ${spacing[4]} ${spacing[4]} ${spacing[8]}`,
        }}
      >
        {!isWarning && (
          <Stack gap={0}>
            <Title
              size="tiny"
              color={colors.text.primaryDefault}
              style={{ paddingTop: spacing[4] }}
            >
              {title}
            </Title>
            {subtitle && (
              <Text size="small" color={colors.text.secondaryDefault}>
                {subtitle}
              </Text>
            )}
          </Stack>
        )}
        <IconButton variant="secondary" aria-label={closeText} onClick={onClose}>
          <IconX size={16} />
        </IconButton>
      </Group>

      <Flex
        style={{
          padding: `${spacing[2]} ${spacing[8]} ${spacing[4]}`,
          minHeight: 0,
          overflowY: "auto",
        }}
        gap={isWarning ? spacing[4] : spacing[5]}
        direction={!isWarning && orientation === "horizontal" ? "row" : "column"}
        align={isWarning ? "center" : undefined}
      >
        {isWarning ? (
          <Stack align="center" gap={spacing[4]} w="100%">
            {icon}
            <Stack align="center" gap={spacing[1]} w="100%">
              <Title
                size="tiny"
                color={colors.text.primaryDefault}
                style={{ textAlign: "center", width: "100%" }}
              >
                {title}
              </Title>
              {children}
            </Stack>
          </Stack>
        ) : (
          children
        )}
      </Flex>

      {isWarning ? (
        <Stack
          gap={spacing[3]}
          style={{
            flexShrink: 0,
            padding: `${spacing[4]} ${spacing[8]} ${spacing[8]}`,
          }}
        >
          {primaryButton}
          {secondaryButton}
        </Stack>
      ) : (
        <Group
          justify="flex-end"
          gap={spacing[3]}
          style={{
            flexShrink: 0,
            padding: `${spacing[4]} ${spacing[8]} ${spacing[8]}`,
          }}
        >
          {secondaryButton}
          {primaryButton}
        </Group>
      )}
    </MantineModal>
  );
};

export { Modal };
